/**
 * Auth storage: sessionStorage only, minimal data (userId).
 * All auth-related keys cleared on logout for both sessionStorage and localStorage (legacy).
 */

export const AUTH_STORAGE_KEYS = [
  'userDetails',
  'userRole',
  'currentUser',
  'authUserId',
] as const;

const USER_ID_KEY = 'authUserId';

export function getStoredUserId(): string | null {
  return sessionStorage.getItem(USER_ID_KEY);
}

export function setStoredUserId(userId: string): void {
  sessionStorage.setItem(USER_ID_KEY, userId);
}

export function clearStoredUserId(): void {
  sessionStorage.removeItem(USER_ID_KEY);
}

/** Custom event dispatched when auth is cleared (401, 404, logout) so AuthContext can clear in-memory state. */
export const AUTH_SIGNOUT_EVENT = 'auth:signOut';

/** When true, interceptor should not show toasts for 401 (e.g. during intentional idle/manual logout). */
let suppressAuthToasts = false;
export function setSuppressAuthToasts(value: boolean): void {
  suppressAuthToasts = value;
}
export function getSuppressAuthToasts(): boolean {
  return suppressAuthToasts;
}

/** Clears all auth-related keys from both sessionStorage and localStorage (logout / 401). */
export function clearAllAuthStorage(): void {
  for (const key of AUTH_STORAGE_KEYS) {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  }
  window.dispatchEvent(new CustomEvent(AUTH_SIGNOUT_EVENT));
}

/** Returns true if the error is Cognito "Refresh Token has been revoked" (NotAuthorizedException). */
export function isRevokedRefreshTokenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const msg =
    (error as { message?: string }).message ??
    (error as { body?: { message?: string } }).body?.message ??
    '';
  const name = (error as { name?: string }).name ?? (error as { __type?: string }).__type ?? '';
  return (
    name === 'NotAuthorizedException' ||
    /Refresh Token has been revoked/i.test(String(msg))
  );
}
